import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { API, http, getToken, setToken, errorText } from "@/lib/api";
import { AdminLogin } from "./AdminLogin";
import { PostEditor } from "./PostEditor";
import { PostList } from "./PostList";

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/posts");
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) { setToken(null); setUser(null); }
      else toast.error(errorText(err));
    }
  }, []);

  useEffect(() => {
    document.title = "Admin — Scale Solutions by Sadiya";
    if (!API || !getToken()) { setChecking(false); return undefined; }
    http.get("/auth/me").then((r) => setUser(r?.data || null)).catch(() => setToken(null)).finally(() => setChecking(false));
    return undefined;
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    setToken(null);
    setUser(null);
  };

  const remove = async (p) => {
    try {
      await http.delete(`/admin/posts/${p.id}`);
      toast.success("Article deleted");
      load();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  if (checking) return <div className="min-h-screen bg-[#16302B]" />;
  if (!user) return <AdminLogin onLogin={setUser} />;

  return (
    <main className="min-h-screen bg-[#F6F2E8] pt-[72px]" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Journal admin</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-3 text-[#221F1B]">Insights</h1>
            <p className="text-[14px] text-[#5C564E] mt-2">Signed in as {user.email}</p>
          </div>
          <div className="flex items-center gap-5">
            <button onClick={logout} className="text-[14px] text-[#5C564E] link-underline" data-testid="admin-logout-button">Sign out</button>
            {editing === null && (
              <button onClick={() => setEditing({})} className="btn-brass px-5 py-3 text-[13px] font-medium inline-flex items-center gap-2" data-testid="admin-new-post-button">
                <Plus className="h-4 w-4" strokeWidth={1.5} /> New article
              </button>
            )}
          </div>
        </div>
        <div className="mt-12">
          {editing !== null ? (
            <PostEditor post={editing.id ? editing : null} onDone={() => { setEditing(null); load(); }} />
          ) : (
            <PostList posts={posts} onEdit={setEditing} onDelete={remove} />
          )}
        </div>
      </div>
    </main>
  );
}
